import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { EpisodeView } from "@supermanus/document-model";
import {
  formatDocumentEpisodeLine,
  formatDocumentTimestamp,
} from "./DocumentHead";

export interface PrintPageChromeProps {
  landscape?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  view: EpisodeView;
  /** Timestamp frozen for this print session. */
  printedAt: Date;
}

type PageSlot = {
  page: number;
  pageTop: number;
  pageHeight: number;
  padTop: number;
  padBottom: number;
};

/**
 * Running header/footer for the physical printout only.
 * Placed in the paper padding bands (absolute, per page) so content is never covered.
 * Hidden on screen (Forbered udskrift + working views) via `.sm-print-only`.
 */
export function PrintPageChrome({
  landscape = false,
  productionTitle,
  seasonTitle,
  episodeTitle,
  view,
  printedAt,
}: PrintPageChromeProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState<PageSlot[]>([]);
  const when = useMemo(
    () => formatDocumentTimestamp(printedAt),
    [printedAt],
  );
  const episodeLine = useMemo(
    () => formatDocumentEpisodeLine(seasonTitle, episodeTitle, view),
    [seasonTitle, episodeTitle, view],
  );

  useLayoutEffect(() => {
    const root = rootRef.current;
    const probe = probeRef.current;
    if (!root || !probe) return;

    const paper = root.closest(".sm-paper") as HTMLElement | null;
    if (!paper) return;

    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const pageHeight = probe.offsetHeight;
        if (pageHeight <= 1) {
          setSlots([]);
          return;
        }
        const cs = getComputedStyle(paper);
        const padTop = cssLenToPx(cs.paddingTop, paper);
        const padBottom = cssLenToPx(cs.paddingBottom, paper);
        const totalH = Math.max(pageHeight, paper.scrollHeight);
        const pageCount = Math.max(1, Math.ceil(totalH / pageHeight - 1e-6));
        const next: PageSlot[] = [];
        for (let page = 1; page <= pageCount; page++) {
          next.push({
            page,
            pageTop: (page - 1) * pageHeight,
            pageHeight,
            padTop,
            padBottom,
          });
        }
        setSlots((prev) =>
          prev.length === next.length &&
          prev.every(
            (v, i) =>
              v.page === next[i]?.page &&
              Math.abs(v.pageTop - (next[i]?.pageTop ?? 0)) < 0.5 &&
              Math.abs(v.pageHeight - (next[i]?.pageHeight ?? 0)) < 0.5,
          )
            ? prev
            : next,
        );
      });
    };

    const ro = new ResizeObserver(update);
    ro.observe(paper);
    ro.observe(probe);
    const mo = new MutationObserver(update);
    mo.observe(paper, {
      subtree: true,
      childList: true,
      characterData: true,
    });
    update();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, [landscape]);

  const total = Math.max(1, slots.length);

  return (
    <div
      ref={rootRef}
      className="sm-print-page-chrome sm-print-only"
      aria-hidden="true"
      data-landscape={landscape ? "1" : "0"}
    >
      <div
        ref={probeRef}
        className={
          landscape
            ? "sm-print-page-probe sm-print-page-probe--landscape"
            : "sm-print-page-probe"
        }
      />

      {slots.map((slot) => {
        const headerTop = slot.pageTop + Math.max(2, slot.padTop * 0.15);
        const footerTop =
          slot.pageTop + slot.pageHeight - Math.max(14, slot.padBottom * 0.75);
        return (
          <div key={slot.page} className="sm-print-page-slot">
            <div
              className="sm-print-page-header sm-doc-head-row"
              style={{ top: headerTop }}
            >
              <div className="sm-doc-head-main">
                <strong>{productionTitle}</strong>
                <span>{episodeLine}</span>
              </div>
              <time dateTime={printedAt.toISOString()}>{when}</time>
            </div>

            <div
              className="sm-print-page-footer"
              style={{ top: footerTop }}
            >
              Side {slot.page} af {total}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function cssLenToPx(value: string, ctx: HTMLElement): number {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "0") return 0;
  const px = Number.parseFloat(trimmed);
  if (trimmed.endsWith("px") && Number.isFinite(px)) return px;
  if (Number.isFinite(px) && !/[a-z%]/i.test(trimmed)) return px;
  const probe = document.createElement("div");
  probe.style.cssText = `position:absolute;visibility:hidden;height:${trimmed};width:0;pointer-events:none`;
  ctx.appendChild(probe);
  const measured = probe.offsetHeight;
  probe.remove();
  return measured > 0 ? measured : Number.isFinite(px) ? px : 0;
}
